import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListGroup } from 'react-bootstrap';
import { useNavigationData } from '../modules/NavigationProvider';
import { usePolesContext } from '../modules/poles/PolesContext';
import { useEquipmentContext } from '../modules/equipment/EquipmentContext';
import { useDataContext } from '../modules/data/DataContext';
import { Firmware } from '../modules/poles/_models';
import { Data } from '../modules/data/_models';

const EquipmentsPage: FC = () => {
  const { t } = useTranslation('equipments');
  const { setPath, setButtonText, setHeaderText } = useNavigationData();
  const { currentPole } = usePolesContext();
  const { equipment } = useEquipmentContext();
  const { lastData } = useDataContext();
  const [selected, setSelected] = useState<number | null>(null);

  useLayoutEffect(() => {
    setPath('/menu');
    setButtonText();
    setHeaderText(t('title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(() => {
    if (!currentPole?.firmware) return [];

    return Object.keys(currentPole.firmware)
      .filter(
        (k) =>
          k.startsWith('in') || k.startsWith('24out') || k.startsWith('12out')
      )
      .map((key) => {
        const item = currentPole.firmware[key as keyof Firmware] as {
          name: string;
        };

        const status = lastData
          ? (lastData[key as keyof Data] as { value: number }).value ??
            (lastData[key as keyof Data] as { current: number }).current
          : 0;

        return {
          name: item.name,
          key,
          status,
        };
      });
  }, [currentPole?.firmware, lastData]);

  const menus = useMemo(
    () =>
      equipment && equipment.length
        ? equipment
            .filter((e) => items.find((i) => i.name === e.name))
            .reduce<Record<string, { name: string; status: boolean }[]>>(
              (acc, item) => {
                const status = !!items.find((i) => i.name === item.name)
                  ?.status;

                if (acc[item.type]) {
                  acc[item.type].push({ name: item.name, status });
                } else {
                  acc[item.type] = [{ name: item.name, status }];
                }

                return acc;
              },
              {
                antenas: [
                  { name: t('antenas.lte'), status: !!lastData?.gsm?.value },
                  {
                    name: t('antenas.satellite'),
                    status: !!lastData?.gps?.satellites,
                  },
                ],
              }
            )
        : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [equipment, items, lastData?.gps?.satellites, lastData?.gsm?.value]
  );

  const handleClick = (index: number) => {
    setSelected(selected === index ? null : index);
  };

  return (
    <div className="d-flex justify-content-center align-items-center h-100 mt-5">
      <div
        className="text-uppercase"
        style={{
          width: '200px',
          opacity: Object.keys(menus)?.length ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      >
        <ListGroup>
          {Object.keys(menus).map((item, index) => (
            <React.Fragment key={index}>
              <ListGroup.Item
                className="d-flex justify-content-between"
                onClick={() => handleClick(index)}
              >
                {t(`${item}.label`)}
              </ListGroup.Item>
              {menus[item].length ? (
                // Render the submenu inline to push other items downward
                <ListGroup
                  className={`submenu-container ${
                    selected === index ? 'open' : ''
                  }`}
                >
                  {menus[item].map((subItem, subIndex) => (
                    <ListGroup.Item
                      key={subIndex}
                      className="d-flex  justify-content-between ms-3"
                    >
                      <span>{subItem.name}</span>
                      <i
                        className={`bi ${
                          subItem.status
                            ? 'bi-check-circle-fill text-success'
                            : 'bi-slash-circle-fill text-danger'
                        }`}
                      ></i>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : null}
            </React.Fragment>
          ))}
        </ListGroup>
      </div>
    </div>
  );
};

export default EquipmentsPage;
